import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { eur } from "@/components/LineItemsEditor";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Undo2, Loader2, Plus } from "lucide-react";
import { toast } from "sonner";

export default function Avoirs() {
  const [invoices, setInvoices] = useState([]);
  const [credits, setCredits] = useState([]);
  const [refunds, setRefunds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dlg, setDlg] = useState(null); // 'deposit' | 'credit' | 'refund'
  const [form, setForm] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      setInvoices((await api.get("/invoices")).data || []);
      setCredits((await api.get("/credit-notes")).data || []);
      setRefunds((await api.get("/refunds")).data || []);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const submit = async () => {
    const iid = form.invoice_id;
    if (!iid) { toast.error("Facture requise"); return; }
    try {
      if (dlg === "deposit") await api.post(`/invoices/${iid}/deposit`, { amount: Number(form.amount), method: form.method });
      if (dlg === "credit") await api.post(`/invoices/${iid}/credit-note`, { amount: form.full ? null : Number(form.amount), full: !!form.full, reason: form.reason });
      if (dlg === "refund") await api.post(`/invoices/${iid}/refund`, { amount: Number(form.amount), method: form.method, reason: form.reason });
      toast.success("Enregistré"); setDlg(null); setForm({}); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-emerald-600" /></div>;
  const titles = { deposit: "Enregistrer un acompte", credit: "Créer un avoir", refund: "Enregistrer un remboursement" };

  return (
    <div>
      <PageHeader title="Acomptes · Avoirs · Remboursements" subtitle="Gestion comptable rattachée à vos factures" icon={Undo2}
        actions={<div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={() => { setForm({}); setDlg("deposit"); }} data-testid="btn-deposit" className="border-slate-200">Acompte</Button>
          <Button size="sm" variant="outline" onClick={() => { setForm({}); setDlg("credit"); }} data-testid="btn-credit" className="border-slate-200">Avoir</Button>
          <Button size="sm" onClick={() => { setForm({}); setDlg("refund"); }} data-testid="btn-refund" className="bg-emerald-500 hover:bg-emerald-600 text-black font-semibold"><Plus className="w-4 h-4 mr-1" />Remboursement</Button>
        </div>} />

      <Tabs defaultValue="credits">
        <TabsList className="mb-4"><TabsTrigger value="credits" data-testid="avoirs-tab-credits">Avoirs</TabsTrigger><TabsTrigger value="refunds" data-testid="avoirs-tab-refunds">Remboursements</TabsTrigger></TabsList>
        <TabsContent value="credits">
          {credits.length === 0 ? <Empty>Aucun avoir.</Empty> :
            <Table rows={credits} cols={[["number", "N°"], ["invoice_number", "Facture"], ["date", "Date"], ["amount", "Montant", true], ["reason", "Motif"]]} />}
        </TabsContent>
        <TabsContent value="refunds">
          {refunds.length === 0 ? <Empty>Aucun remboursement.</Empty> :
            <Table rows={refunds} cols={[["invoice_number", "Facture"], ["date", "Date"], ["amount", "Montant", true], ["method", "Moyen"], ["reason", "Motif"]]} />}
        </TabsContent>
      </Tabs>

      <Dialog open={!!dlg} onOpenChange={(v) => !v && setDlg(null)}>
        <DialogContent className="bg-white border-slate-200 max-w-sm">
          <DialogHeader><DialogTitle>{titles[dlg]}</DialogTitle><DialogDescription className="sr-only">Formulaire comptable</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5"><Label className="text-xs text-slate-500">Facture</Label>
              <Select value={form.invoice_id || ""} onValueChange={(v) => setForm({ ...form, invoice_id: v })}><SelectTrigger data-testid="avoir-invoice" className="bg-slate-50 border-slate-200"><SelectValue placeholder="Sélectionner" /></SelectTrigger>
                <SelectContent className="bg-white border-slate-200 max-h-[40vh]">{invoices.map((i) => <SelectItem key={i.id} value={i.id}>{i.number} · {i.client_name || ""} · {eur(i.total || 0)}</SelectItem>)}</SelectContent></Select></div>
            {dlg === "credit" && <label className="flex items-center gap-2 text-sm text-slate-600"><input type="checkbox" checked={!!form.full} onChange={(e) => setForm({ ...form, full: e.target.checked })} data-testid="avoir-full" className="accent-emerald-500" /> Avoir total (montant de la facture)</label>}
            {!(dlg === "credit" && form.full) && <Input type="number" placeholder="Montant €" value={form.amount || ""} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="avoir-amount" className="bg-slate-50 border-slate-200" />}
            {dlg !== "credit" && <Input placeholder="Moyen (CB, espèces, virement…)" value={form.method || ""} onChange={(e) => setForm({ ...form, method: e.target.value })} className="bg-slate-50 border-slate-200" />}
            {dlg !== "deposit" && <Input placeholder="Motif" value={form.reason || ""} onChange={(e) => setForm({ ...form, reason: e.target.value })} className="bg-slate-50 border-slate-200" />}
          </div>
          <DialogFooter><Button onClick={submit} data-testid="avoir-save" className="bg-emerald-500 hover:bg-emerald-600 text-black font-semibold">Enregistrer</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const Empty = ({ children }) => <div className="bg-white border border-slate-200 rounded-xl p-10 text-center text-slate-500">{children}</div>;
function Table({ rows, cols }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl overflow-hidden"><div className="overflow-x-auto"><table className="w-full text-sm">
      <thead className="bg-slate-50 text-slate-500 text-xs uppercase"><tr>{cols.map((c) => <th key={c[0]} className={`px-4 py-3 ${c[2] ? "text-right" : "text-left"}`}>{c[1]}</th>)}</tr></thead>
      <tbody className="divide-y divide-slate-200">{rows.map((r) => (
        <tr key={r.id} data-testid={`avoir-row-${r.id}`}>{cols.map((c) => <td key={c[0]} className={`px-4 py-3 ${c[2] ? "text-right font-mono" : "text-slate-600"}`}>{c[2] ? eur(r[c[0]] || 0) : (r[c[0]] || "—")}</td>)}</tr>
      ))}</tbody></table></div></div>
  );
}
